import { log, logV } from "../../shared/services/debug-logger";
import { useAdminAgentPanelResize } from "../hooks/useAdminAgentPanelResize";
import { adminAgentPanelOpenAtom } from "../state/admin-agent.atoms";
import { ADMIN_AGENT_PANEL_ID } from "../utils/admin-agent.constants";
import AdminAgentPanel from "./AdminAgentPanel";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { useAtom } from "jotai";
import { type CSSProperties, useRef } from "react";

// Reserve a desktop column so the administrator can keep using the current admin page.
// Keep the closed chat as a small icon tab on the right edge, clear of page header controls.
// Closing the panel stops browser polling, while backend runs continue independently.
const AdminAgentWorkspace = () => {
  const [isOpen, setIsOpen] = useAtom(adminAgentPanelOpenAtom);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const resize = useAdminAgentPanelResize();

  const closePanel = () => {
    log("AdminAgentWorkspace - closePanel");
    resize.finishResize();
    setIsOpen(false);
    window.requestAnimationFrame(() => toggleRef.current?.focus());
  };

  const openPanel = () => {
    log("AdminAgentWorkspace - openPanel");
    setIsOpen(true);
  };
  logV("AdminAgentWorkspace - BUILD", { isOpen });

  return (
    <div
      data-test="admin-agent-dock"
      style={{ "--admin-agent-panel-width": `${resize.width}px` } as CSSProperties}
      className={`relative h-full shrink-0 ${isOpen ? "w-0 lg:w-[var(--admin-agent-panel-width)]" : "w-0"}`}
    >
      <button
        ref={toggleRef}
        type="button"
        data-test="admin-agent-open"
        aria-controls={ADMIN_AGENT_PANEL_ID}
        aria-expanded={isOpen}
        aria-label="Open admin assistant"
        title="Open admin assistant"
        onClick={openPanel}
        className={
          isOpen
            ? "hidden"
            : "fixed bottom-6 right-0 z-30 flex h-8 w-8 items-center justify-center rounded-l-lg border border-r-0 border-white/20 bg-green text-white shadow-md transition-colors hover:bg-[#166b60] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white"
        }
      >
        <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      {isOpen && <AdminAgentPanel onClose={closePanel} resize={resize} />}
    </div>
  );
};

export default AdminAgentWorkspace;
